import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Button } from "@/components/ui/button";
import { useToast } from "@/app/Toast";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Link } from "react-router-dom";

interface Recurring {
  id: number;
  publicCode: string;
  locationId: number;
  rrule: string;
  dtstart: string;
  dtend: string | null;
  horizonDays: number;
  status: "ACTIVE" | "PAUSED" | "CANCELLED";
  createdAt: string;
}

export function RecurringPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Recurring[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<Recurring[]>("/api/v1/recurring");
      setRows(r);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load";
      setError(message);
      toast("error", message);
    } finally { setLoading(false); }
  }

  useEffect(() => { void load(); }, []);

  async function cancelSeries(r: Recurring) {
    if (!confirm(`Cancel recurring ${r.publicCode}? Future occurrences will be marked CANCELLED.`)) return;
    try {
      const res = await api.delete<{ occurrencesCancelled: number }>(`/api/v1/recurring/${r.id}`);
      toast("success", `Series cancelled (${res.occurrencesCancelled} future occurrence(s))`);
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Cancel failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Recurring bookings</h1>
        <p className="text-sm text-muted-foreground">
          Configure in <Link to="/settings/recurring" className="text-primary hover:underline">Settings → Recurring</Link>.
        </p>
      </div>
      {error && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"><span>{error}</span><Button size="sm" variant="outline" onClick={() => void load()}>Try again</Button></div>}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active series</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <Table className="min-w-[680px]">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>RRULE</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead>Horizon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono">{r.publicCode}</TableCell>
                  <TableCell className="max-w-md truncate font-mono text-xs">{r.rrule}</TableCell>
                  <TableCell>{formatDateTime(r.dtstart)}</TableCell>
                  <TableCell>{r.horizonDays}d</TableCell>
                  <TableCell>
                    {r.status === "ACTIVE" ? <span className="text-emerald-600">ACTIVE</span> :
                     r.status === "CANCELLED" ? <Badge>CANCELLED</Badge> :
                     <Badge>PAUSED</Badge>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {r.status === "ACTIVE" && (
                      <Button size="sm" variant="outline" onClick={() => void cancelSeries(r)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Loading recurring series…</TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No recurring series. Create one from Settings → Recurring.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
